'use client';

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { ArrowLeft } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAppSelector, useAppDispatch } from '@/store';
import { clearForm } from '@/store/property-create-slice';
import { FormProvider, useForm } from 'react-hook-form';
import { useCreatePropertyMutation } from '@/hooks/queries/useProperties';
import {
  transformFormDataToApiRequest,
  type PropertyCreateFormData,
} from '@/lib/property-creation-utils';
import { PropertyConfirmation } from '@/components/properties/create/property-confirmation';
import { PropertyCreateProgress } from '@/components/properties/create/property-create-progress';

export default function PropertyCreateStep3Page() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const isRTL = i18n.language === 'ar';
  const dispatch = useAppDispatch();

  const persisted = useAppSelector(
    state => state.propertyCreate?.formData || {}
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const createPropertyMutation = useCreatePropertyMutation({
    onSuccessCallback: () => {
      // Clear form data from store after successful creation
      dispatch(clearForm());
      navigate('/properties');
    },
  });

  const handleCancel = () => navigate('/properties');
  const handleBack = () => navigate('/properties/create/2');

  const methods = useForm({
    defaultValues: persisted,
    mode: 'onTouched',
  });

  const { handleSubmit: rhfHandleSubmit } = methods;

  const onSubmit = async () => {
    setIsSubmitting(true);
    try {
      // Transform all collected data to API request format
      const requestData = await transformFormDataToApiRequest(
        persisted as PropertyCreateFormData
      );
      await createPropertyMutation.mutateAsync(requestData);

      toast({
        title: t('properties.create.toasts.createdTitle'),
        description: t('properties.create.toasts.createdDesc', {
          name: persisted.name,
          admin: persisted.contactPerson,
        }),
      });
    } catch (error) {
      console.error('Error creating property:', error);
      toast({
        title: t('properties.create.toasts.errorTitle'),
        description: t('properties.create.toasts.errorDesc'),
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header */}
      <div className="bg-primary text-primary-foreground p-4 sm:p-6">
        <div className="max-w-6xl mx-auto">
          <div
            className={cn(
              'flex items-center gap-4',
              isRTL ? 'flex-row-reverse' : 'flex-row'
            )}
          >
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCancel}
              className="text-primary-foreground hover:bg-primary-foreground/10"
            >
              <ArrowLeft className={cn('w-4 h-4', isRTL ? 'ml-2' : 'mr-2')} />
              {t('back')}
            </Button>
            <h1
              className={cn(
                'text-xl sm:text-2xl font-semibold',
                isRTL ? 'text-right' : 'text-left'
              )}
            >
              {t('properties.create.title')}
            </h1>
            <Badge
              variant="secondary"
              className={cn(isRTL ? 'mr-auto' : 'ml-auto')}
            >
              {t('properties.create.badgeRole')}
            </Badge>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-4 sm:p-6">
        {/* Progress Steps */}
        <PropertyCreateProgress currentStep={3} />

        <FormProvider {...methods}>
          <form onSubmit={rhfHandleSubmit(onSubmit)}>
            {/* Step Content */}
            <div className="space-y-6">
              <PropertyConfirmation />
            </div>

            {/* Navigation Buttons */}
            <div
              className={cn(
                'flex gap-4 pt-8 border-t mt-8',
                isRTL ? 'flex-row-reverse' : 'flex-row'
              )}
            >
              <Button
                type="button"
                variant="outline"
                onClick={handleBack}
                disabled={isSubmitting}
                className="w-full sm:w-auto"
              >
                <ArrowLeft className={cn('w-4 h-4', isRTL ? 'ml-2' : 'mr-2')} />
                {t('previous')}
              </Button>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="bg-accent hover:bg-accent/90 text-accent-foreground w-full sm:w-auto"
              >
                {isSubmitting
                  ? t('properties.create.buttons.creating')
                  : t('properties.create.buttons.create')}
              </Button>

              <Button
                type="button"
                variant="ghost"
                onClick={handleCancel}
                disabled={isSubmitting}
                className="w-full sm:w-auto"
              >
                {t('cancel')}
              </Button>
            </div>
          </form>
        </FormProvider>
      </div>
    </div>
  );
}
